/* iter-p2-d — Feature-video registry hook.
 *
 * A tiny module-level cache in front of `GET /api/feature-videos` so
 * that every HelpTip on the page shares one network request. Exposes
 * `useFeatureVideoUrl(slug)` for the HelpTip → Watch button and
 * `useFeatureVideoCatalog()` for the admin grid.
 *
 * Cache TTL is 5 minutes; admin saves broadcast a `pw:feature-videos-changed`
 * window event that invalidates the cache immediately.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';
const TTL_MS = 5 * 60 * 1000;

let _cache = null;      // { videos, catalog, at }
let _inflight = null;   // Promise
const _subs = new Set();

function _notify() { _subs.forEach((fn) => { try { fn(_cache); } catch { /* ignore */ } }); }

export function invalidateFeatureVideosCache() {
  _cache = null;
  _inflight = null;
  _notify();
  try { window.dispatchEvent(new CustomEvent('pw:feature-videos-changed')); } catch { /* ignore */ }
}

async function _fetch() {
  if (_cache && (Date.now() - _cache.at) < TTL_MS) return _cache;
  if (_inflight) return _inflight;
  _inflight = axios.get(`${API}/feature-videos`)
    .then((r) => {
      _cache = {
        videos:  r.data?.videos  || {},
        catalog: r.data?.catalog || [],
        at:      Date.now(),
      };
      _inflight = null;
      _notify();
      return _cache;
    })
    .catch(() => {
      _inflight = null;
      _cache = { videos: {}, catalog: [], at: Date.now() };
      _notify();
      return _cache;
    });
  return _inflight;
}

function _useCache() {
  const [state, setState] = useState(_cache);
  useEffect(() => {
    let alive = true;
    const onExternal = () => { if (alive) _fetch().then((c) => alive && setState(c)); };
    _subs.add(setState);
    window.addEventListener('pw:feature-videos-changed', onExternal);
    if (!_cache) _fetch().then((c) => { if (alive) setState(c); });
    return () => {
      alive = false;
      _subs.delete(setState);
      window.removeEventListener('pw:feature-videos-changed', onExternal);
    };
  }, []);
  return state;
}

/** URL for one slug (or empty string when nothing is bound). */
export function useFeatureVideoUrl(slug) {
  const s = _useCache();
  if (!s || !slug) return '';
  return (s.videos && s.videos[slug]) || '';
}

/** Full catalog + current URL map for the admin grid. */
export function useFeatureVideoCatalog() {
  const s = _useCache();
  return {
    catalog: s?.catalog || [],
    videos:  s?.videos  || {},
    loading: !s,
  };
}
